import { Lifecycle } from "./Lifecycle";
import { SOFTR_PAGE_RENDERER_DOM_CONTENT_LOADED, SOFTR_PAGE_RENDERER_LOAD } from "./events";

/**
 * Run the provided function with the patched load listener.
 * The patch updates window and document's addEventListener method to subscribe to
 * {@link SOFTR_PAGE_RENDERER_DOM_CONTENT_LOADED} event instead of the native
 * DOMContentLoaded or load events. Other addEventListener calls are passed through
 * to the unpatched methods.
 *
 * This way, the custom code that listens to these events will have access to
 * 1. The block root dom elements
 * 2. Global state that other custom scripts have initialized.
 *
 * The actual event is triggered in {@link SoftrPageRenderer.render} after all scripts have been executed.
 */
export async function withPatchedLoadListener(lifecycle: Lifecycle, f: () => Promise<void> | void): Promise<void> {
    const restoreWindowPatch = patchLoadListener(lifecycle, window);
    const restoreDocumentPatch = patchLoadListener(lifecycle, document);
    try {
        await f();
    } catch (e) {
        console.error("Error executing script", e);
    } finally {
        restoreDocumentPatch();
        restoreWindowPatch();
    }
}

const EVENT_PATCHES: Record<string, string | undefined> = {
    DOMContentLoaded: SOFTR_PAGE_RENDERER_DOM_CONTENT_LOADED,
    load: SOFTR_PAGE_RENDERER_LOAD,
};
function patchLoadListener(
    /**
     * Lifecycle of the custom event listeners.
     * Since we're attaching listeners to the window and document objects,
     * we need to clean them up when the page is disposed.
     * This is not the same as the lifetime of the patch itself, which
     * is very short lived, and is cleaned up in the finlally block above.
     */
    lifecycle: Lifecycle,
    target: Window | Document,
) {
    const originalListener = target.addEventListener;
    target.addEventListener = function (
        eventType: string,
        listener: EventListenerOrEventListenerObject,
        options?: boolean | AddEventListenerOptions,
    ) {
        const patchedEventType = EVENT_PATCHES[eventType];
        if (patchedEventType) {
            target.addEventListener(patchedEventType, listener, options);
            lifecycle.push(() => {
                target.removeEventListener(patchedEventType, listener);
            });
        } else {
            originalListener.call(target, eventType, listener, options);
        }
    };
    const patchedAddEventListener = target.addEventListener;
    return () => {
        if (target.addEventListener === patchedAddEventListener) {
            target.addEventListener = originalListener;
        } else {
            console.error(
                "addEventListener was patched by another script; Skipping the restoration of the page renderer patch.",
            );
        }
    };
}
